/* ==========================================================================
   WAYPOINT — zero-bloat global store on useSyncExternalStore. No zustand,
   no redux. A single mutable state object + a Set of listeners.
   ========================================================================== */

import { useSyncExternalStore } from 'react';
import * as api from './api';
import { rollupStopPlaces } from '../features/trips/lib/places';
import {
  applyTheme,
  loadThemeChoice,
  msUntilNextThemeFlip,
  persistThemeChoice,
  resolveTheme,
  type ResolvedTheme,
  type ThemeChoice,
} from './theme';
import type {
  Activity,
  AuthUser,
  ChatUnreadEntry,
  Media,
  MapModeValue,
  Memory,
  Stop,
  Trip,
  TripSummary,
  RemoveStopResult,
  SwapStopResult,
} from './types';

// mapMode has its own tiny type here to avoid a circular import; re-exported
// below for convenience.
export type MapMode = MapModeValue;

const MAP_MODE_KEY = 'waypoint:mapMode';
const CINEMATIC_FLAG_KEY = 'waypoint:cinematicEnabled';

export interface StoreState {
  authUser: AuthUser | null;
  authChecked: boolean;
  trips: TripSummary[];
  activeTrip: Trip | null;
  media: Record<string, Media[]>; // keyed by tripId
  mapMode: MapMode;
  /** Feature flag for the three.js Cinematic globe, off by default (Settings
   *  → "Cinematic 3D globe"). Performance is the map's everyday renderer;
   *  Cinematic is opt-in and, while off, `mapMode` is not honoured at all —
   *  see effectiveMapMode() below, which is what the map actually reads. */
  cinematicEnabled: boolean;
  /** What the user PICKED (Light / Dark / Follow the time). */
  themeChoice: ThemeChoice;
  /** What that currently resolves to — the value stamped on <html>. Under
   *  'auto' this moves on its own at the day/night boundary (see the timer
   *  in startThemeWatcher), which is why it's stored rather than derived at
   *  each read: components need to re-render when it flips. */
  theme: ResolvedTheme;
  chatUnread: ChatUnreadEntry[]; // powers the Scooty tab badge (BottomNav)
}

/** Performance is the default now (it was 'cinematic'): the vector map is
 *  the one that's zero-network, easy on battery, and legible in daylight,
 *  and Cinematic sits behind its own flag. A stored 'cinematic' from before
 *  that change is still honoured — but only once the flag is on. */
function loadMapMode(): MapMode {
  if (typeof localStorage === 'undefined') return 'performance';
  return localStorage.getItem(MAP_MODE_KEY) === 'cinematic' ? 'cinematic' : 'performance';
}

function loadCinematicEnabled(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(CINEMATIC_FLAG_KEY) === '1';
}

const initialThemeChoice = loadThemeChoice();

let state: StoreState = {
  authUser: null,
  authChecked: false,
  trips: [],
  activeTrip: null,
  media: {},
  mapMode: loadMapMode(),
  cinematicEnabled: loadCinematicEnabled(),
  themeChoice: initialThemeChoice,
  theme: resolveTheme(initialThemeChoice),
  chatUnread: [],
};

const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function setState(patch: Partial<StoreState>) {
  state = { ...state, ...patch };
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): StoreState {
  return state;
}

/** React hook: subscribe the whole store. Prefer the selector hooks below
 * in hot paths to avoid over-rendering. */
export function useStore(): StoreState {
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function useStoreSelector<T>(selector: (s: StoreState) => T): T {
  return useSyncExternalStore(subscribe, () => selector(getSnapshot()));
}

// ---------------------------------------------------------------------------
// mergeEntity — used by optimistic tool_result merges from the agent SSE
// stream, and by every plain REST action below.
// ---------------------------------------------------------------------------

export function mergeEntity(type: 'trip', data: Trip): void;
export function mergeEntity(type: 'stop', data: Stop): void;
export function mergeEntity(type: 'activity', data: Activity): void;
export function mergeEntity(
  type: 'trip' | 'stop' | 'activity',
  data: Trip | Stop | Activity,
): void {
  if (type === 'trip') {
    const trip = data as Trip;
    const activeTrip =
      state.activeTrip && state.activeTrip.id === trip.id
        ? { ...state.activeTrip, ...trip }
        : state.activeTrip;
    setState({ activeTrip });
    return;
  }

  if (!state.activeTrip) return;

  if (type === 'stop') {
    const stop = data as Stop;
    const existing = state.activeTrip.stops;
    const idx = existing.findIndex((s) => s.id === stop.id);
    const stops =
      idx === -1
        ? [...existing, stop].sort((a, b) => a.seq - b.seq)
        : existing.map((s) => (s.id === stop.id ? stop : s));
    setState({ activeTrip: { ...state.activeTrip, stops } });
    return;
  }

  if (type === 'activity') {
    const activity = data as Activity;
    const existing = state.activeTrip.activities;
    const idx = existing.findIndex((a) => a.id === activity.id);
    const activities =
      idx === -1
        ? [...existing, activity]
        : existing.map((a) => (a.id === activity.id ? activity : a));
    setState({ activeTrip: { ...state.activeTrip, activities } });
  }
}

export function removeStop(stopId: string): void {
  if (!state.activeTrip) return;
  setState({
    activeTrip: {
      ...state.activeTrip,
      stops: state.activeTrip.stops.filter((s) => s.id !== stopId),
    },
  });
}

export function removeActivity(activityId: string): void {
  if (!state.activeTrip) return;
  setState({
    activeTrip: {
      ...state.activeTrip,
      activities: state.activeTrip.activities.filter((a) => a.id !== activityId),
    },
  });
}

// ---------------------------------------------------------------------------
// Auth actions
// ---------------------------------------------------------------------------

export async function checkAuth(): Promise<AuthUser | null> {
  try {
    const me = await api.getMe();
    setState({ authUser: me, authChecked: true });
    return me;
  } catch {
    setState({ authUser: null, authChecked: true });
    return null;
  }
}

export async function loginAction(userId: string, pin: string): Promise<void> {
  await api.login(userId, pin);
  await checkAuth();
}

export async function logoutAction(): Promise<void> {
  await api.logout();
  setState({ authUser: null, activeTrip: null, trips: [] });
}

// ---------------------------------------------------------------------------
// Trips actions
// ---------------------------------------------------------------------------

export async function loadTrips(): Promise<TripSummary[]> {
  const trips = await api.getTrips();
  setState({ trips });
  return trips;
}

export async function loadTrip(id: string): Promise<Trip> {
  const trip = await api.getTrip(id);
  setState({ activeTrip: trip });
  return trip;
}

export function clearActiveTrip(): void {
  setState({ activeTrip: null });
}

export async function createTripAction(input: Parameters<typeof api.createTrip>[0]) {
  const trip = await api.createTrip(input);
  setState({ trips: [...state.trips, tripToSummary(trip)] });
  return trip;
}

/** Drops a deleted trip from the list and clears activeTrip if it's the one
 * showing — the caller (Trip Detail's delete flow) navigates away right
 * after this, but a stale activeTrip would otherwise still be visible to
 * anything reading the store between the delete resolving and the route
 * change landing. */
export function removeTrip(id: string): void {
  setState({
    trips: state.trips.filter((t) => t.id !== id),
    activeTrip: state.activeTrip?.id === id ? null : state.activeTrip,
  });
}

export async function patchTripAction(
  id: string,
  input: Parameters<typeof api.patchTrip>[1],
): Promise<Trip> {
  const trip = await api.patchTrip(id, input);
  mergeEntity('trip', trip);
  setState({
    trips: state.trips.map((t) => (t.id === id ? { ...t, ...tripToSummary(trip) } : t)),
  });
  return trip;
}

function tripToSummary(trip: Trip): TripSummary {
  const existing = state.trips.find((t) => t.id === trip.id);
  return {
    id: trip.id,
    title: trip.title,
    description: trip.description,
    status: trip.status,
    // The derived-state block travels with the trip — synthesizing a summary must not drop it,
    // or a freshly-patched trip's card would render an empty badge until the next full load.
    state: trip.state,
    stateReason: trip.stateReason,
    stateOverride: trip.stateOverride,
    stateOverrideBy: trip.stateOverrideBy,
    stateOverrideAt: trip.stateOverrideAt,
    dateSource: trip.dateSource,
    destination: trip.destination,
    approvalStatus: trip.approvalStatus,
    reviewRequestedBy: trip.reviewRequestedBy,
    approvedBy: trip.approvedBy,
    startDate: trip.startDate,
    endDate: trip.endDate,
    coverMediaId: trip.coverMediaId,
    stopCount: trip.stops.length,
    activityCount: trip.activities.length,
    mediaCount: trip.mediaCount ?? existing?.mediaCount ?? 0,
    totalMiles: existing?.totalMiles ?? 0,
    // Recomputed from the stops rather than carried over from `existing`, because a write is
    // exactly when these change: adding a stop in a new state, or renaming one from "site 24" to
    // "Kooser State Park", has to move the card's location line and its state-park count in the
    // same breath. Same ordering rules as the server's own rollup — see lib/places.ts.
    ...rollupStopPlaces(trip.stops),
    updatedAt: trip.updatedAt,
  };
}

// ---------------------------------------------------------------------------
// Stops & activities actions (REST path; agent path merges via mergeEntity
// directly from the SSE handler in features/chat)
// ---------------------------------------------------------------------------

export async function addStopAction(
  tripId: string,
  input: Parameters<typeof api.addStop>[1],
  opts: api.StopWriteOptions = {},
): Promise<Stop> {
  const stop = await api.addStop(tripId, input, opts);
  mergeEntity('stop', stop);
  return stop;
}

export async function patchStopAction(
  id: string,
  input: Parameters<typeof api.patchStop>[1],
  opts: api.StopWriteOptions = {},
): Promise<Stop> {
  const stop = await api.patchStop(id, input, opts);
  mergeEntity('stop', stop);
  return stop;
}

export async function deleteStopAction(id: string, opts: api.StopWriteOptions = {}): Promise<void> {
  await api.deleteStop(id, opts);
  removeStop(id);
}

/** Road edits (docs/ROAD-EDITS.md §5). Both re-fetch the trip afterwards rather than patching the
 *  store by hand: a remove re-chains the NEXT stop's leg and a swap renumbers seq, and the numbers
 *  the screen paints red must be the server's, not a guess. */
export async function removeStopAction(id: string, opts: api.StopWriteOptions = {}): Promise<RemoveStopResult> {
  const result = await api.removeStop(id, opts);
  removeStop(id);
  const tripId = state.activeTrip?.id;
  if (tripId) await loadTrip(tripId);
  return result;
}

export async function swapStopAction(
  id: string,
  proposalId: string,
  opts: api.StopWriteOptions = {},
): Promise<SwapStopResult> {
  const result = await api.swapStop(id, proposalId, opts);
  const tripId = state.activeTrip?.id;
  if (tripId) await loadTrip(tripId);
  return result;
}

export async function addActivityAction(
  tripId: string,
  input: Parameters<typeof api.addActivity>[1],
): Promise<Activity> {
  const activity = await api.addActivity(tripId, input);
  mergeEntity('activity', activity);
  return activity;
}

export async function patchActivityAction(
  id: string,
  input: Parameters<typeof api.patchActivity>[1],
): Promise<Activity> {
  const activity = await api.patchActivity(id, input);
  mergeEntity('activity', activity);
  return activity;
}

export async function deleteActivityAction(id: string): Promise<void> {
  await api.deleteActivity(id);
  removeActivity(id);
}

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

export async function loadMedia(tripId: string): Promise<Media[]> {
  const media = await api.getMedia(tripId);
  setState({ media: { ...state.media, [tripId]: media } });
  return media;
}

export function mergeMedia(tripId: string, item: Media): void {
  const existing = state.media[tripId] ?? [];
  const idx = existing.findIndex((m) => m.id === item.id);
  const next = idx === -1 ? [item, ...existing] : existing.map((m) => (m.id === item.id ? item : m));
  setState({ media: { ...state.media, [tripId]: next } });
}

/** Drops the photo server-side, then removes it from the cached grid and decrements the trip's
 *  mediaCount in both the summary list and the open trip, so the "N photos" stat and the grid
 *  agree without a refetch. */
export async function removeMedia(tripId: string, mediaId: string): Promise<void> {
  await api.deleteMedia(mediaId);
  const existing = state.media[tripId] ?? [];
  const decrement = (count: number | undefined) => Math.max(0, (count ?? 0) - 1);
  setState({
    media: { ...state.media, [tripId]: existing.filter((m) => m.id !== mediaId) },
    trips: state.trips.map((t) => (t.id === tripId ? { ...t, mediaCount: decrement(t.mediaCount) } : t)),
    activeTrip:
      state.activeTrip && state.activeTrip.id === tripId
        ? {
            ...state.activeTrip,
            mediaCount: decrement(state.activeTrip.mediaCount),
            coverMediaId: state.activeTrip.coverMediaId === mediaId ? null : state.activeTrip.coverMediaId,
          }
        : state.activeTrip,
  });
}

/** Saves a caption/tag/stop edit and swaps the returned photo into the cached grid. The server
 *  re-resolves the caption on the way back, so clearing the caption field lands here as the
 *  regenerated auto caption rather than an empty one. */
export async function patchMediaAction(
  tripId: string,
  mediaId: string,
  input: Parameters<typeof api.patchMedia>[1],
): Promise<Media> {
  const updated = await api.patchMedia(mediaId, input);
  mergeMedia(tripId, updated);
  return updated;
}

// ---------------------------------------------------------------------------
// Memories (docs/USER-EDITING.md §3.3)
// ---------------------------------------------------------------------------

/** The nullable-`date`/`stopId` shape `PartialMemoryInput` (lib/types.ts) doesn't offer.
 *  `CreateMemoryInput` — and `PartialMemoryInput`, which is just `Partial<CreateMemoryInput>` —
 *  was written for memory CREATION, where omitting a key already means "use the default" and
 *  there was never a need to say "clear this on purpose". Editing does need that: unsetting a
 *  memory's date, or detaching it from a stop, are both `null`, not omission — the same
 *  distinction `CreateStopInput` already draws for its own date fields. lib/types.ts is out of
 *  scope for this change, so the cast in patchMemoryAction below papers over the gap instead of
 *  widening it there. */
export interface MemoryPatchInput {
  text?: string;
  mediaIds?: string[];
  stopId?: string | null;
  date?: string | null;
  videoUrl?: string | null;
}

/** Unlike every *Action above, this is a THIN WRAPPER with no `mergeEntity` call — there is no
 *  memory slice in this store to merge into. Memories live in `features/memories/index.tsx`'s
 *  module-level `memoriesCache` and in `TripDetail`'s local `textMemories` state, neither of
 *  which this file can see or update. The caller owns invalidating/refreshing whichever of those
 *  it's holding after a successful edit — forgetting to is a stale-cache bug, not a store bug. */
export async function patchMemoryAction(id: string, input: MemoryPatchInput): Promise<Memory> {
  return api.patchMemory(id, input);
}

/** Same caveat as patchMemoryAction: nothing here to update, because nothing here holds
 *  memories. The caller drops the memory from its own cache/list once this resolves. */
export async function deleteMemoryAction(id: string): Promise<void> {
  await api.deleteMemory(id);
}

// ---------------------------------------------------------------------------
// Map mode (persisted)
// ---------------------------------------------------------------------------

export function setMapMode(mode: MapMode): void {
  try {
    localStorage.setItem(MAP_MODE_KEY, mode);
  } catch {
    // storage unavailable (private mode) — in-memory state still updates
  }
  setState({ mapMode: mode });
}

/** The map mode that's actually in force. Turning the Cinematic flag off
 *  must not silently strand someone on a globe they can no longer switch
 *  away from, so the flag gates the mode rather than the stored mode being
 *  rewritten — flip the flag back on and the previous choice is still there.
 */
export function effectiveMapMode(s: StoreState): MapMode {
  return s.cinematicEnabled ? s.mapMode : 'performance';
}

export function setCinematicEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(CINEMATIC_FLAG_KEY, enabled ? '1' : '0');
  } catch {
    // storage unavailable (private mode) — in-memory state still updates
  }
  // Turning the flag ON is also the moment someone means "show me the
  // globe" — there's no separate mode control any more (ModeToggle is gone
  // from the map), so the flag IS the switch.
  setState({ cinematicEnabled: enabled, mapMode: enabled ? 'cinematic' : state.mapMode });
}

// ---------------------------------------------------------------------------
// Theme (persisted) — see lib/theme.ts for what 'auto' means
// ---------------------------------------------------------------------------

export function setThemeChoice(choice: ThemeChoice): void {
  persistThemeChoice(choice);
  const resolved = resolveTheme(choice);
  applyTheme(resolved);
  setState({ themeChoice: choice, theme: resolved });
  scheduleThemeFlip();
}

/** Re-resolve the current choice and re-stamp <html>. A no-op for the
 *  explicit choices; the whole point under 'auto'. */
function refreshTheme(): void {
  const resolved = resolveTheme(state.themeChoice);
  applyTheme(resolved);
  if (resolved !== state.theme) setState({ theme: resolved });
}

let themeTimer: number | undefined;

/** One timer aimed at the next day/night boundary, rearmed each time it
 *  fires — not a poll. Also re-resolves whenever the tab regains focus,
 *  which is what catches a phone that was asleep across the boundary (a
 *  background timer is not reliable there, and the app coming back cream at
 *  9pm is exactly the bug "follow the time" exists to avoid). */
function scheduleThemeFlip(): void {
  if (typeof window === 'undefined') return;
  window.clearTimeout(themeTimer);
  if (state.themeChoice !== 'auto') return;
  themeTimer = window.setTimeout(() => {
    refreshTheme();
    scheduleThemeFlip();
  }, msUntilNextThemeFlip());
}

/** Called once from main.tsx, before React mounts. */
export function startThemeWatcher(): void {
  applyTheme(state.theme);
  scheduleThemeFlip();
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    refreshTheme();
    scheduleThemeFlip();
  });
}

// ---------------------------------------------------------------------------
// Chat unread (Scooty tab badge — polled ~20s + on focus by BottomNav)
// ---------------------------------------------------------------------------

export async function loadChatUnread(): Promise<ChatUnreadEntry[]> {
  const { trips } = await api.getChatUnread();
  const safe = trips ?? [];
  setState({ chatUnread: safe });
  return safe;
}

/** Zeroes out one trip's unread count locally the instant it's marked
 * read, rather than waiting up to 20s for the next poll to catch up. */
export function clearChatUnread(tripId: string): void {
  if (!state.chatUnread.some((t) => t.tripId === tripId && (t.unread > 0 || t.mentioned))) return;
  setState({
    chatUnread: state.chatUnread.map((t) => (t.tripId === tripId ? { ...t, unread: 0, mentioned: false } : t)),
  });
}
